import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  clearWabaToast,
  setPendingOpenChat,
  sendMessage,
  attachMetaMessageId,
} from "../../data/redux/slices/WabaChatSlice";
import api from "../../axios/axiosInstance";

const AUTO_DISMISS_MS = 8000;
const TOAST_HEIGHT = 210; // approximate px per toast
const TOAST_GAP = 10;

function WabaToastItem({ toast, index, chatRoute, dispatch, navigate }) {
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);

  useEffect(() => {
    // Reset timer each time a new message arrives
    clearInterval(timerRef.current);
    setProgress(100);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const pct = Math.max(0, 100 - ((Date.now() - start) / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct <= 0) {
        clearInterval(timerRef.current);
        dispatch(clearWabaToast(toast.from));
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [toast.from, toast.messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const pauseTimer = () => clearInterval(timerRef.current);

  const resumeTimer = () => {
    const remaining = (progress / 100) * AUTO_DISMISS_MS;
    const start = Date.now();
    const currentPct = progress;
    timerRef.current = setInterval(() => {
      const pct = Math.max(0, currentPct - ((Date.now() - start) / remaining) * currentPct);
      setProgress(pct);
      if (pct <= 0) {
        clearInterval(timerRef.current);
        dispatch(clearWabaToast(toast.from));
      }
    }, 50);
  };

  const handleOpenChat = () => {
    dispatch(setPendingOpenChat(toast.from));
    dispatch(clearWabaToast(toast.from));
    navigate(chatRoute);
  };

  const handleReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    const tempId = `tmp_${Date.now()}`;
    try {
      dispatch(sendMessage({ chatId: toast.from, text: replyText, type: "text", name: toast.senderName, tempId }));
      const resp = await api.post("/api/whatsapp/send-text", { to: toast.from, text: replyText, name: toast.senderName });
      const metaMessageId = resp?.data?.data?.messages?.[0]?.id;
      if (metaMessageId) dispatch(attachMetaMessageId({ chatId: toast.from, tempId, metaMessageId }));
      dispatch(clearWabaToast(toast.from));
    } catch {
      setSending(false);
    }
  };

  const bottom = 24 + index * (TOAST_HEIGHT + TOAST_GAP);

  return (
    <div
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      style={{
        position: "fixed",
        bottom,
        right: 24,
        width: 320,
        borderRadius: 14,
        background: "#1a2e22",
        border: "1px solid rgba(37,211,102,0.25)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        overflow: "hidden",
        zIndex: 99999,
        fontFamily: "inherit",
        transition: "bottom 0.25s ease",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px 8px" }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "#25d366", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#25d366", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {toast.senderName || "WhatsApp"}
          </div>
          <div style={{ color: "#6b7f6e", fontSize: 11 }}>+{toast.from}</div>
        </div>
        <button onClick={() => dispatch(clearWabaToast(toast.from))} style={{ background: "none", border: "none", color: "#6b7f6e", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}>×</button>
      </div>

      {/* Messages */}
      <div style={{ padding: "0 14px 10px", maxHeight: 120, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
        {(toast.messages || []).map((msg, i) => (
          <div key={i} style={{ color: "#c8e6c9", fontSize: 13, lineHeight: 1.4, background: "rgba(255,255,255,0.05)", borderRadius: 6, padding: "4px 8px" }}>
            {msg}
          </div>
        ))}
      </div>

      {/* Reply */}
      <div style={{ padding: "0 10px 10px", display: "flex", gap: 6 }}>
        <input
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleReply()}
          placeholder="Reply…"
          style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(37,211,102,0.2)", borderRadius: 8, padding: "6px 10px", color: "#e8f5e9", fontSize: 12, outline: "none" }}
        />
        <button onClick={handleReply} disabled={!replyText.trim() || sending}
          style={{ background: "#25d366", border: "none", borderRadius: 8, padding: "6px 12px", color: "white", fontSize: 12, fontWeight: 600, cursor: replyText.trim() ? "pointer" : "default", opacity: !replyText.trim() || sending ? 0.5 : 1, flexShrink: 0 }}>
          {sending ? "…" : "Send"}
        </button>
      </div>

      {/* Open chat */}
      <div style={{ padding: "0 14px 10px" }}>
        <button onClick={handleOpenChat} style={{ background: "none", border: "none", color: "#25d366", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
          Open chat →
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ height: 3, background: "rgba(37,211,102,0.15)" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "#25d366", transition: "width 50ms linear" }} />
      </div>
    </div>
  );
}

export default function WabaMessageToast() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toasts = useSelector((state) => state.wabaChat.incomingToasts);
  const role = useSelector((state) => state.profile.role);

  const chatRoute =
    role === "admin" ? "/admin/whatsapp/chat"
    : role === "user" ? "/agent/whatsapp"
    : "/whatsapp";

  if (!toasts || toasts.length === 0) return null;

  return (
    <>
      {toasts.map((toast, i) => (
        <WabaToastItem
          key={toast.from}
          toast={toast}
          index={i}
          chatRoute={chatRoute}
          dispatch={dispatch}
          navigate={navigate}
        />
      ))}
    </>
  );
}
